/**
 * Sync Engine — manages data synchronization between IndexedDB and API
 * 
 * Strategy:
 * - READ: IndexedDB first (instant), then API refresh in background
 * - WRITE (online): API first, then update IndexedDB
 * - WRITE (offline): IndexedDB + outbox, sync when online
 */

import { apiFetch } from './api';
import * as db from './offlineDB';

type StoreName = 'customers' | 'invoices' | 'vendors' | 'expenses' | 'accounts' | 'payments' | 'bills' | 'settings';

const STORE_API_MAP: Record<StoreName, string> = {
  customers: '/api/customers',
  invoices: '/api/invoices',
  vendors: '/api/vendors',
  expenses: '/api/expenses',
  accounts: '/api/accounts',
  payments: '/api/payments',
  bills: '/api/bills',
  settings: '/api/system/settings',
};

// Cache freshness: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

let syncInProgress = false;
let onlineListenerAdded = false;

/**
 * Fetch data with offline-first strategy:
 * 1. Return cached data immediately (if available)
 * 2. Fetch fresh data from API in background
 * 3. Update cache
 * 4. Call onFresh callback with new data
 */
export async function fetchWithCache(
  store: StoreName,
  options?: {
    forceRefresh?: boolean;
    onFresh?: (data: any[]) => void;
  }
): Promise<any[]> {
  const { forceRefresh = false, onFresh } = options || {};

  // 1. Get cached data
  let cached: any[] = [];
  try {
    cached = await db.getAll(store);
  } catch { /* IndexedDB not available */ }

  const lastSync = await db.getLastSync(store).catch(() => 0);
  const isFresh = Date.now() - lastSync < CACHE_TTL;

  // 2. If cache is fresh and not forcing refresh, return cached
  if (cached.length > 0 && isFresh && !forceRefresh) {
    // Still refresh in background silently
    refreshInBackground(store, onFresh);
    return cached;
  }

  // 3. Try to fetch from API
  if (navigator.onLine) {
    try {
      const res = await apiFetch(STORE_API_MAP[store]);
      if (res.ok) {
        const fresh = await res.json();
        const data = Array.isArray(fresh) ? fresh : [];
        // Update cache
        await db.putAll(store, data).catch(() => {});
        await db.setLastSync(store).catch(() => {});
        if (onFresh) onFresh(data);
        return data;
      }
    } catch {
      // Network error — fall through to cached
    }
  }

  // 4. Return cached (even if stale) when offline
  return cached;
}

/**
 * Background refresh — doesn't block UI
 */
async function refreshInBackground(store: StoreName, onFresh?: (data: any[]) => void) {
  if (!navigator.onLine) return;
  try {
    const res = await apiFetch(STORE_API_MAP[store]);
    if (res.ok) {
      const fresh = await res.json();
      const data = Array.isArray(fresh) ? fresh : [];
      await db.putAll(store, data).catch(() => {});
      await db.setLastSync(store).catch(() => {});
      if (onFresh) onFresh(data);
    }
  } catch { /* Silent fail */ }
}

/**
 * Save data with offline support:
 * - Online: API first → update cache
 * - Offline: Save to cache + outbox → sync later
 */
export async function saveWithSync(
  store: StoreName,
  method: 'POST' | 'PUT' | 'DELETE',
  url: string,
  body?: any,
): Promise<{ ok: boolean; data?: any; offline?: boolean }> {
  
  if (navigator.onLine) {
    // Online: send to API
    try {
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        // Update local cache
        if (method === 'DELETE' && body?.id) {
          await db.remove(store, body.id).catch(() => {});
        } else if (data?.id) {
          await db.put(store, data).catch(() => {});
        }
        return { ok: true, data };
      }
      return { ok: false };
    } catch {
      // Network error — fall through to offline mode
    }
  }

  // Offline: save locally + queue for sync
  if (method === 'POST' || method === 'PUT') {
    const item = { ...body, id: body?.id || `offline_${Date.now()}`, _pendingSync: true };
    await db.put(store, item).catch(() => {});
  } else if (method === 'DELETE' && body?.id) {
    await db.remove(store, body.id).catch(() => {});
  }

  // Add to outbox
  await db.addToOutbox({ store, method, url, body }).catch(() => {});

  return { ok: true, offline: true, data: body };
}

/**
 * Sync all pending outbox items to server
 * Called when coming back online
 */
export async function syncOutbox(): Promise<{ synced: number; failed: number }> {
  if (syncInProgress || !navigator.onLine) return { synced: 0, failed: 0 };
  syncInProgress = true;

  let synced = 0;
  let failed = 0;

  try {
    const pending = await db.getPendingOutbox();

    for (const item of pending) {
      try {
        const res = await apiFetch(item.url, {
          method: item.method,
          headers: { 'Content-Type': 'application/json' },
          body: item.body ? JSON.stringify(item.body) : undefined,
        });

        if (res.ok) {
          await db.markSynced(item.id);
          synced++;
          // Update local item (remove _pendingSync flag)
          if (item.body?.id) {
            const fresh = await res.json().catch(() => item.body);
            await db.put(item.store, { ...fresh, _pendingSync: undefined }).catch(() => {});
          }
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
    }

    // Clean up synced items
    await db.clearSyncedOutbox().catch(() => {});
  } finally {
    syncInProgress = false;
  }

  return { synced, failed };
}

/**
 * Initialize sync engine — listen for online events
 */
export function initSyncEngine() {
  if (onlineListenerAdded) return;
  onlineListenerAdded = true;

  window.addEventListener('online', async () => {
    console.debug('[SyncEngine] Back online — syncing outbox...');
    const result = await syncOutbox();
    if (result.synced > 0) {
      console.debug(`[SyncEngine] Synced ${result.synced} items`);
    }
  });
}

/**
 * Get sync status
 */
export async function getSyncStatus(): Promise<{
  pendingCount: number;
  lastSync: Record<string, number>;
  isOnline: boolean;
}> {
  const pendingCount = await db.getOutboxCount().catch(() => 0);
  const lastSync: Record<string, number> = {};
  
  for (const store of ['customers', 'invoices', 'vendors'] as StoreName[]) {
    lastSync[store] = await db.getLastSync(store).catch(() => 0);
  }

  return {
    pendingCount,
    lastSync,
    isOnline: navigator.onLine,
  };
}
